import React, { useEffect, useState } from 'react'
import { Box, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import { PricingAvailabilityItem } from '@src/aem-content/aem-types'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import HandleMarkup from '@src/components/HandleMarkup'
import { Field } from 'formik'
import LiquidEmeraldQuantityAdapter from '@src/components/LiquidEmeraldQuantityAdapter'
import LiquidQuantityInputAdapter from '@src/components/LiquidQuantityInputAdapter'
import DiscoPromoAddToCartButton, {
  Colors,
  Variants,
} from './DiscoPromoAddCartButton'

export interface AddToCartFormValues {
  [key: string]: {
    materialNumber: string
    quantity: number | string
  }
}

type DiscoPromoMobileMaterialPricingProps = {
  product?: string
  materialPricing: PricingAvailabilityItem[]
  dchainMessage?: string
  submitForm: () => void
  isSubmitting: boolean
  dirty: boolean
  resetForm: (nextState?: { values?: AddToCartFormValues }) => void
  setFieldValue: (field: string, value: any) => void
  values: AddToCartFormValues
}

const useStyles = makeStyles((theme) => ({
  MaterialPricingInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(4),
  },
  label: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightMedium,
    lineHeight: '20px',
  },
  packSizeText: {
    display: 'block',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
  },
  priceText: {
    display: 'block',
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightRegular,
    lineHeight: '20px',
  },
  quantityAddtoCartContainer: {
    display: 'flex',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: theme.spacing(3),
  },
  qtyInput: {
    display: 'flex',
    alignItems: 'center',
    fontSize: theme.typography.pxToRem(14),
    maxWidth: '99px',
    minWidth: '99px',
    height: '48px',
    '& svg': {
      fill: theme.palette.primary.main,
    },
  },
  addToCartButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    alignSelf: 'stretch',
    lineHeight: '24px',
    height: '48px',
    width: '220px',
    minWidth: '144px',
    padding: theme.spacing(3, 8),
    [theme.breakpoints.down(374)]: {
      width: '189px',
    },
    [theme.breakpoints.down(343)]: {
      maxWidth: '189px',
      width: '100%',
    },
  },
}))

const DiscoPromoMobileMaterialPricing: React.FC<
  DiscoPromoMobileMaterialPricingProps
> = ({
  materialPricing,
  dchainMessage,
  submitForm,
  isSubmitting,
  dirty,
  setFieldValue,
  values,
}) => {
  const classes = useStyles()
  const { formatMessage } = useIntl()
  const [minQtyErrors, setMinQtyErrors] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (
      dchainMessage === null &&
      materialPricing.every((item) => item?.availableQtyInStock === null)
    ) {
      Object.keys(values).forEach((key) => {
        setFieldValue(key, {
          ...values[key],
          quantity: '1',
        })
      })
    }
  }, [])

  const shouldDisabledAddToCartBtn = Object.values(minQtyErrors).some(Boolean)

  const onMinimumOrderQtyChange = (name: string, hasError: boolean) => {
    setMinQtyErrors((prev) => ({ ...prev, [name]: hasError }))
  }

  /* show List Price, List Price not available show Price */
  const effectivePrice = (material: PricingAvailabilityItem) =>
    material.listPrice ? (
      <FormattedNumber
        style="currency"
        currencyDisplay="symbol"
        currency={material.listPriceCurrency}
        value={material.listPrice}
      />
    ) : material.price ? (
      <FormattedNumber
        style="currency"
        currencyDisplay="symbol"
        currency={material.currency}
        value={material.price}
      />
    ) : (
      <FormattedMessage id="UNAVAILABLE" defaultMessage="Unavailable" />
    )

  return (
    <Box>
      {materialPricing?.map((material, index) => (
        <Box
          key={`${material.materialNumber}-${index}`}
          className={classes.MaterialPricingInfo}
        >
          <Box>
            <Typography component="label" className={classes.label}>
              {formatMessage({ id: 'PACK_SIZE', defaultMessage: 'Pack Size' })}
            </Typography>
            <Typography component="span" className={classes.packSizeText}>
              <HandleMarkup value={material?.packageSize} />
            </Typography>
          </Box>
          <Box>
            <Typography component="label" className={classes.label}>
              {formatMessage({ id: 'PRICE', defaultMessage: 'Price' })}
            </Typography>
            <Typography component="span" className={classes.priceText}>
              {effectivePrice(material)}
            </Typography>
          </Box>

          {/* Quantity + Add to cart */}
          <Box className={classes.quantityAddtoCartContainer}>
            <Box>
              {material.minOrderQuantity && material.minOrderQuantity > 1 ? (
                <Field
                  name={`${index}.quantity`}
                  component={LiquidEmeraldQuantityAdapter}
                  className={classes.qtyInput}
                  data-testid={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  id={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  associatedComponent={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  materialNumber={material.materialNumber}
                  minOrderQuantity={material.minOrderQuantity}
                  setMultipleMinimumQuantityError={onMinimumOrderQtyChange}
                />
              ) : (
                <Field
                  name={`${index}.quantity`}
                  component={LiquidQuantityInputAdapter}
                  className={classes.qtyInput}
                  data-testid={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  id={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  associatedComponent={`P&A-row-quantity-${material.brand}-${material.materialNumber}`}
                  max={material?.availableQtyInStock ?? 9999}
                />
              )}
            </Box>
            <Box>
              <DiscoPromoAddToCartButton
                size="large"
                variant={Variants.Contained}
                addToCartButtonClass={classes.addToCartButton}
                disabled={shouldDisabledAddToCartBtn}
                dirty={dirty}
                handleAddToCart={(e) => {
                  e.preventDefault()
                  submitForm()
                }}
                isSubmitting={isSubmitting}
                color={Colors.Secondary}
              />
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  )
}

export default DiscoPromoMobileMaterialPricing
